import { FC, useEffect, useMemo, useState } from 'react';
import { components as rsComponents } from 'react-select';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';

import { Validation } from '@/models';
import {
  AlertBox,
  AMLCheckTabs,
  Button,
  ChainSelector,
  ConfirmActionModal,
  Input,
  InputSelect,
  SectionBox,
  SelectOption,
  SvgIcon,
} from '@/components';
import {
  useApiCall,
  useDebounce,
  useInputBool,
  useInputText,
  useModal,
} from '@/hooks';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { amlCheckActions, profileActions } from '@/redux/actions';
import {
  amlCheckSelectors,
  externalServiceSelectors,
  profileSelectors,
  serviceOptionsSelectors,
  userSelectors,
} from '@/redux/selectors';
import {
  ChainOptions,
  ChainsDictionary,
  ChainsEnum,
  ErrorCodeEnum,
  DEBOUNCE_DELAY,
  investigationsPagePath,
} from '@/config';
import {
  amlbotAPI,
  AmlCheckRequest,
  AssetTokenRequest,
  AssetTokenResponse,
  BonusItem,
  BonusTypeEnum,
  WhichHashRequest,
} from '@/api';
import { showEmailConfirmationToast } from '@/helpers';

import styles from './InvestigateAddress.module.sass';
import { OnlyForVerifiedSection } from '../OnlyForVerifiedSection/OnlyForVerifiedSection';

interface InvestigateAddressProps {}

const DEFAULT_NETWORK_TOKEN_ID = '0';

const InvestigateAddress: FC<InvestigateAddressProps> = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [whichLoading, setWhichLoading] = useState(false);
  const {
    loading,
    error: amlError,
    errorCode: amlErrorCode,
  } = useAppSelector(amlCheckSelectors.getAmlCheckSlice);
  const { data: serviceOptions } = useAppSelector(
    serviceOptionsSelectors.getServiceOptionsSelectors,
  );
  const devModeUsed = useAppSelector(userSelectors.checkIfDevModeUsed);
  const presetedFlowModes = useAppSelector(profileSelectors.presetedFlowModes);
  const isEmailConfirmed = useAppSelector(userSelectors.checkIfEmailConfirmed);
  const { data: profileData } = useAppSelector(
    profileSelectors.getProfileSlice,
  );
  const { data: userData } = useAppSelector(userSelectors.getUserSlice);
  const helpcrunchLoaded = useAppSelector(
    externalServiceSelectors.helpcrunchLoaded,
  );
  const isAccountLiteMode = useAppSelector(profileSelectors.checkIfModeLimited);

  const [flatAssetTokens, setFlatAssetTokens] = useState<SelectOption[]>([]);
  const [tokenFilter, setTokenFilter] = useState({ page: 1, term: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [hasMoreTokens, setHasMoreTokens] = useState(true);
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [selectedToken, setSelectedToken] = useState<SelectOption | null>(null);

  const bonusInvestigationNote = useMemo(() => {
    if (profileData?.bonuses.length) {
      const amount = profileData?.bonuses.reduce(
        (sum: number, bonus: BonusItem) => {
          sum +=
            bonus.type === BonusTypeEnum.TYPE_INVESTIGATION
              ? Number(bonus.value)
              : 0;
          return sum;
        },
        0,
      );

      if (amount > 0) {
        return t('translation.bonus_investigation_note', { count: amount });
      }
    }
    return '';
  }, [t, profileData?.bonuses]);

  const assetTokens = useMemo(
    () => (flatAssetTokens.length ? flatAssetTokens : []),
    [flatAssetTokens],
  );

  const HASH_VALIDATIONS: Validation[] = useMemo(() => {
    const validations: Validation[] = [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
    ];
    return validations;
  }, [t]);

  const CHAIN_VALIDATIONS: Validation[] = useMemo(() => {
    const validations: Validation[] = [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
    ];

    return validations;
  }, [t]);

  const TOKEN_VALIDATIONS: Validation[] = useMemo(() => {
    const validations: Validation[] = [
      {
        name: 'minLength',
        value: 0,
        message: t('translation.field_is_required'),
      },
    ];

    return validations;
  }, [t]);

  const FORCE_VALIDATIONS: Validation[] = useMemo(() => [], []);

  const hashField = useInputText('', HASH_VALIDATIONS);
  const chainField = useInputText('', CHAIN_VALIDATIONS);
  const tokenField = useInputText(DEFAULT_NETWORK_TOKEN_ID, TOKEN_VALIDATIONS);
  const forceField = useInputBool(false, FORCE_VALIDATIONS);

  const {
    isOpen: isOpenConfirmModal,
    onOpenModal: handleOpenConfirmModal,
    onCloseModal: handleCloseConfirmModal,
  } = useModal();

  const precheckRequired = amlErrorCode === ErrorCodeEnum.PRECHECK_REQUIRED;

  const checkHandler = () => {
    if (isEmailConfirmed) {
      if (!loading) {
        if (hashField.isValid && chainField.isValid) {
          const payload: AmlCheckRequest = {
            hash: hashField.value,
            asset: chainField.value as ChainsEnum,
            token: Number(tokenField.value) || 0,
            force: forceField.value,
          };
          dispatch(amlCheckActions.investigate(payload));
          forceField.onReset();
        } else {
          if (!hashField.isValid) {
            hashField.onBlur();
          }
          if (!chainField.isValid) {
            chainField.onBlur();
          }
        }
      }
    } else {
      showEmailConfirmationToast();
    }
  };

  // Effects
  useEffect(() => {
    dispatch(profileActions.getProfileInfo());
    return () => {
      dispatch(amlCheckActions.setErrorCode(null));
      dispatch(amlCheckActions.setError(null));
    };
  }, []);

  useEffect(() => {
    if (serviceOptions) {
      const chainOptions = ChainOptions.filter(
        (opt) => !serviceOptions?.notInvestigatedCoins?.includes(opt.value),
      );

      setOptions(chainOptions);
    }
  }, [serviceOptions]);

  useEffect(() => {
    if (amlErrorCode === ErrorCodeEnum.FORCE_REQUIRED || precheckRequired) {
      handleOpenConfirmModal();
    }
  }, [amlErrorCode]);

  useEffect(() => {
    if (forceField.value) {
      checkHandler();
    }
  }, [forceField.value]);

  useEffect(() => {
    setSuggestions([]);
    chainField.onReset();
  }, [hashField.value]);

  useEffect(() => {
    tokenField.onChange(DEFAULT_NETWORK_TOKEN_ID);
    setFlatAssetTokens([]);
    setTokenFilter({ page: 1, term: '' });
  }, [chainField.value]);

  const [getAssetTokens] = useApiCall(async () => {
    const payload: AssetTokenRequest = {
      asset: chainField.value,
      page: tokenFilter.page,
      term: tokenFilter.term,
    };
    // Optionally [errorCode] - ErrorCodeEnum.FIELD_REQUIRED
    const response: AssetTokenResponse = await amlbotAPI.fetchTokens(payload);

    if (response.success) {
      setHasMoreTokens(!!response.data?.hasMore);

      const tokenList = (response.data?.result || []).map(
        (token): SelectOption => ({
          value: `${token.token_id}`,
          label: `${token.code} <span>[${token.issuer}]</span>`,
          code: token.code,
        }),
      );

      if (response.data?.page === 1) {
        const uniqTokenList = selectedToken
          ? tokenList.filter((token) => token.value !== selectedToken.value)
          : tokenList;
        setFlatAssetTokens([
          {
            value: `0`,
            label: `${ChainsDictionary[chainField.value].symbol} [${t(
              'translation.native_coin',
            )}]`,
            code: chainField.value,
          },
          ...(selectedToken ? [selectedToken] : []),
          ...uniqTokenList,
        ]);
      } else {
        const uniqTokens = selectedToken
          ? tokenList.filter((token) => selectedToken.value !== token.value)
          : tokenList;
        setFlatAssetTokens((current) => [...current, ...uniqTokens]);
      }
    }
  });

  useEffect(() => {
    if (
      !serviceOptions?.hasTokensCoins.includes(chainField.value) ||
      !hasMoreTokens ||
      suggestions.length === 0
    ) {
      return;
    }

    getAssetTokens();
  }, [chainField.value, tokenFilter.page, tokenFilter.term]);

  // Event Handlers
  const searchTermChanged = useDebounce(() => {
    setTokenFilter({ page: 1, term: searchTerm });
    setHasMoreTokens(true);
  });

  const tokenTermChanged = (val: string) => {
    setSearchTerm(val);
    searchTermChanged();
  };

  const handleScrollBottom = () => {
    if (hasMoreTokens) {
      setTokenFilter({ ...tokenFilter, page: tokenFilter.page + 1 });
    }
  };

  const handleTokenOnChange = (val: string) => {
    if (val !== DEFAULT_NETWORK_TOKEN_ID) {
      setSelectedToken(
        () => flatAssetTokens.find((o) => o.value === val) ?? null,
      );
    } else {
      setSelectedToken(null);
    }

    tokenField.onChange(val);
  };

  const debouncedWhichHash = useDebounce(async () => {
    if (!hashField.value) {
      setOptions((prevOptions) => {
        return prevOptions.map((option) => {
          return {
            ...option,
            isDisabled: false,
          };
        });
      });
      return;
    }

    try {
      setWhichLoading(true);
      const payload: WhichHashRequest = {
        hash: hashField.value,
      };
      const response = await amlbotAPI.getWhichHash(payload);

      const {
        data: {
          suggestions: { addresses: suggestedChains },
        },
      } = response;

      setSuggestions(suggestedChains);

      setOptions((prevOptions) => {
        return prevOptions.map((option) => {
          return {
            ...option,
            isDisabled: !suggestedChains.includes(option.value as ChainsEnum),
          };
        });
      });
      chainField.onReset();
    } catch (error: unknown) {
      // console.log(error);
    } finally {
      setWhichLoading(false);
    }
  }, DEBOUNCE_DELAY);

  const hashFieldChangeHandler = (newValue: string) => {
    if (isEmailConfirmed) {
      hashField.onChange(newValue.trim());
      debouncedWhichHash();
    } else {
      showEmailConfirmationToast();
    }
  };

  const closeConfirmModal = () => {
    forceField.onReset();
    dispatch(amlCheckActions.setErrorCode(null));
    dispatch(amlCheckActions.setError(null));
    handleCloseConfirmModal();
  };

  const triggerForceCheck = () => {
    forceField.onChange(true);
    handleCloseConfirmModal();
  };

  const submitDisabled = () => {
    if (isEmailConfirmed) {
      if (!hashField.value || !chainField.isValid || loading || whichLoading) {
        return true;
      }

      return options.every((option) => option.isDisabled);
    }

    return false;
  };

  const checkButtonText = () => {
    if (loading) return t('translation.btn_checking_state');
    if (whichLoading) return t('translation.btn_validating_state');
    if (!!bonusInvestigationNote) return t('translation.check_for_free_btn');

    return (
      <>
        {t('translation.btn_check_state')}
        <span>
          <SvgIcon name="aml" />
          {serviceOptions?.expandedCheckCost}
        </span>
      </>
    );
  };

  const sectionTitle = t('translation.investigate_addr_title');
  const networkSubtitle = t('translation.specify_network');
  const devModeText = `[${t('translation.dev_mode')}]`;

  return (
    <>
      <AMLCheckTabs activeKey={investigationsPagePath} />

      {isAccountLiteMode && (
        <OnlyForVerifiedSection
          title={t('translation.investigation_limited_title')}
          description={t('translation.investigation_limited_description')}
        />
      )}

      <SectionBox>
        {{
          title: (
            <div className={styles.sectionTitle}>
              <div>{sectionTitle}</div>
              {devModeUsed && (
                <div className={styles.devModeTitle}>{devModeText}</div>
              )}
            </div>
          ),
          body: (
            <>
              <div className={styles.fields}>
                <div className={styles.textarea}>
                  <Input
                    id="hash"
                    name="hash"
                    type="text"
                    value={hashField.value}
                    onChange={hashFieldChangeHandler}
                    onBlur={hashField.onBlur}
                    placeholder={
                      t('translation.investigate_addr_input_placeholder')!
                    }
                    isError={hashField.isDirty && !hashField.isValid}
                    readOnly={loading || whichLoading}
                    disabled={isAccountLiteMode}
                    required
                    clearable
                  />
                  {hashField.isDirty && !hashField.isValid && (
                    <AlertBox type="danger" small>
                      {{
                        description: hashField.errors[0],
                      }}
                    </AlertBox>
                  )}
                </div>
              </div>
              {!!amlError && !amlErrorCode && (
                <AlertBox type="danger" className={styles.message}>
                  {{
                    description: amlError,
                    icon: '🤔',
                  }}
                </AlertBox>
              )}

              <AlertBox type="info" className={styles.message}>
                {{
                  description: parse(t('translation.investigation_warn_str')),
                  icon: '👆',
                }}
              </AlertBox>

              <div className={styles.subtitle}>{networkSubtitle}</div>
              <div className={styles.network}>
                <ChainSelector list={options} onSelect={chainField.onChange} />
              </div>

              {serviceOptions?.hasTokensCoins.includes(chainField.value) &&
                suggestions.length > 0 && (
                  <div className={styles.fields}>
                    <div className={styles.tokens}>
                      <InputSelect
                        id="tokens"
                        name="tokens"
                        classNamePrefix="chain-select"
                        value={tokenField.value}
                        onChange={handleTokenOnChange}
                        onBlur={tokenField.onBlur}
                        options={assetTokens}
                        placeholder="Select"
                        isSearchable
                        clearable
                        disabled={loading || whichLoading}
                        onMenuScrollToBottom={handleScrollBottom}
                        onInputChange={tokenTermChanged}
                        inputValue={searchTerm}
                        formatOptionLabel={(option) => parse(option.label)}
                        components={{
                          SingleValue: ({ data, ...props }) => (
                            <rsComponents.SingleValue data={data} {...props}>
                              {parse(data.label)}
                            </rsComponents.SingleValue>
                          ),
                        }}
                      />
                    </div>
                  </div>
                )}

              <Button
                corner="rect"
                className={styles.check}
                onClick={checkHandler}
                disabled={submitDisabled()}
              >
                {checkButtonText()}
              </Button>

              {!!bonusInvestigationNote && (
                <AlertBox type="info" className={styles.bonusNote}>
                  {{
                    description: bonusInvestigationNote,
                    icon: '👆',
                  }}
                </AlertBox>
              )}
            </>
          ),
        }}
      </SectionBox>

      <ConfirmActionModal
        title={t('translation.alert_warning_title')}
        isOpen={isOpenConfirmModal}
        onClose={closeConfirmModal}
        onConfirm={triggerForceCheck}
        confirmButtonText={t('translation.precheck_agree_msg')}
        cancelButtonText={t('translation.cancel_btn')}
      >
        {precheckRequired && (
          <div>{t('translation.precheck_address_inv_msg')}</div>
        )}
        <div>{t('translation.investigation_sources_warning')}</div>
      </ConfirmActionModal>
    </>
  );
};

export default InvestigateAddress;
